/**
 * changelogFormat.ts — friendly formatting for ChangeLog old/new values.
 *
 * Rules (per DLD §4.3):
 *  - null / ""                     → "—" (muted sentinel, caller must style)
 *  - ISO date YYYY-MM-DD           → locale-formatted date
 *  - ISO datetime YYYY-MM-DDTHH…  → locale-formatted datetime
 *  - Boolean literal "True"/"False"→ translated Yes / No
 *  - Known obligation status enums → human label (not translated legal values)
 *  - Known renewal cycle codes     → human label
 *  - Strings > 80 chars            → truncated (full value in title attr)
 *  - Everything else               → verbatim (preserves legal phrasings)
 *
 * NEVER translate free-text legal VALUES — only format structural codes.
 */

import i18n from "../i18n";
import { formatAmount2 } from "./formatNumber";
import type { ChangeLog } from "./types";

/** Matches ISO date (YYYY-MM-DD) with optional time component. */
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(T[\d:.Z+-]+)?$/;

/** Slash-form date: DD/MM/YYYY — Hightekers backend uses strftime("%d/%m/%Y") */
const SLASH_DATE_RE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

/** Composite status:date — e.g. "confirmed:2026-06-30" */
const COMPOSITE_DATE_RE = /^(confirmed|estimated|needs_confirmation|not_required|under_review|awaiting_data):(\d{4}-\d{2}-\d{2})$/;

/**
 * Fields that semantically hold dates (or status:date composites).
 * Only these fields trigger slash/composite date normalization.
 * This prevents false-positive date formatting on non-date values
 * (e.g. SKUs or version strings that happen to match D/M/YYYY).
 */
const DATE_FIELDS: Set<string> = new Set([
  "obligation.nextDueDate",
  "obligation_due_date",
  "obligation.anchorDate",
  "incorporationDateTyped",
  "eorExpiryDate",
  "eorObtainmentDateTyped",
  "leaseExpiryTyped",
  "licenseRenewal",
  "companyRenewal",
  "agmDate",
  "annualFilingDate",
  "uboDeclaration",
  "mark_renewed",
  "fiscalYearEnd",
]);

/** JSON-blob fields — render a friendly summary instead of raw JSON */
const JSON_BLOB_SUMMARIES: Record<string, string> = {
  "obligation.schedule":  "audit:schedule_changed_summary",
  "phased_schedule":      "audit:phased_schedule_changed_summary",
  "secondary_filings":    "audit:secondary_filings_changed_summary",
};

/** Obligation status codes that map to obligations:status_<code>. */
const OBLIGATION_STATUS_CODES = new Set([
  "confirmed",
  "estimated",
  "needs_confirmation",
  "indefinite",
  "not_required",
  "under_review",
  "awaiting_data",
]);

/** Renewal cycle codes that map to cycles:<code>. */
const CYCLE_CODES = new Set(["1y", "2y", "3y", "4y", "5y", "indefinite"]);

/**
 * Numeric money/percentage fields stored as NUMERIC(x,4) → "50000.0000".
 * Display them with two decimals (fullValue keeps the exact stored string).
 */
const AMOUNT_FIELDS = new Set([
  "shareCapitalAmount",
  "parValueAmount",
  "ownership_stake.percentage",
]);

/** Plain numeric string (optional sign + optional fractional part). */
const PLAIN_NUMBER_RE = /^-?\d+(\.\d+)?$/;

const MAX_LENGTH = 80;

export interface FormattedValue {
  /** The string to render in the UI. */
  display: string;
  /** True when the raw value was null/empty — caller should render a muted "—". */
  isEmpty: boolean;
  /**
   * Original untruncated value (may differ from `display` when truncated).
   * Pass as `title` attr so users can see the full value on hover.
   * Null when `isEmpty` is true.
   */
  fullValue: string | null;
}

/**
 * Format a ChangeLog old/new value for friendly display.
 *
 * @param _field  The ChangeLog `field` key (used for context; values are never
 *                translated — only structural codes are formatted).
 * @param value   The raw string from the DB (may be null).
 */
export function formatChangelogValue(
  _field: string,
  value: string | null,
): FormattedValue {
  if (value === null || value === "") {
    return { display: "—", isEmpty: true, fullValue: null };
  }

  const lang = i18n.language || "en";

  // ── Boolean literals ────────────────────────────────────────────────────────
  if (value === "True" || value === "true") {
    const display = i18n.t("common:yes", { defaultValue: "Yes" });
    return { display, isEmpty: false, fullValue: value };
  }
  if (value === "False" || value === "false") {
    const display = i18n.t("common:no", { defaultValue: "No" });
    return { display, isEmpty: false, fullValue: value };
  }

  // ── JSON-blob friendly summary (Fix 4) ─────────────────────────────────────
  if (_field in JSON_BLOB_SUMMARIES && value && /^[{[]/.test(value)) {
    const display = i18n.t(JSON_BLOB_SUMMARIES[_field], {
      defaultValue:
        _field === "obligation.schedule" ? "Schedule rule changed"
        : _field === "phased_schedule" ? "Phased schedule changed"
        : "Secondary filings changed",
    });
    return { display, isEmpty: false, fullValue: value };
  }

  // ── Date fields: slash-form + composite normalization (Fix 3) ───────────────
  if (DATE_FIELDS.has(_field)) {
    // Composite "status:date" e.g. "confirmed:2026-06-30"
    const compositeMatch = COMPOSITE_DATE_RE.exec(value);
    if (compositeMatch) {
      const statusCode = compositeMatch[1];
      const isoDate = compositeMatch[2];
      try {
        const d = new Date(isoDate);
        if (!isNaN(d.getTime())) {
          const statusLabel = i18n.t(`obligations:status_${statusCode}`, { defaultValue: statusCode });
          const formattedDate = new Intl.DateTimeFormat(lang, {
            day: "numeric", month: "short", year: "numeric",
          }).format(d);
          return { display: `${statusLabel} · ${formattedDate}`, isEmpty: false, fullValue: value };
        }
      } catch { /* fall through */ }
    }
    // Slash-form "DD/MM/YYYY"
    const slashMatch = SLASH_DATE_RE.exec(value);
    if (slashMatch) {
      const day = parseInt(slashMatch[1], 10);
      const month = parseInt(slashMatch[2], 10) - 1; // 0-indexed
      const year = parseInt(slashMatch[3], 10);
      try {
        const d = new Date(year, month, day);
        if (!isNaN(d.getTime())) {
          const display = new Intl.DateTimeFormat(lang, {
            day: "numeric", month: "short", year: "numeric",
          }).format(d);
          return { display, isEmpty: false, fullValue: value };
        }
      } catch { /* fall through */ }
    }
  }

  // ── ISO date / datetime ─────────────────────────────────────────────────────
  if (ISO_DATE_RE.test(value)) {
    try {
      const d = new Date(value);
      if (!isNaN(d.getTime())) {
        const hasTime = value.includes("T");
        const display = hasTime
          ? d.toLocaleString(lang)
          : new Intl.DateTimeFormat(lang, { day: "numeric", month: "short", year: "numeric" }).format(d);
        return { display, isEmpty: false, fullValue: value };
      }
    } catch {
      // fall through to verbatim
    }
  }

  // ── Known obligation status codes ───────────────────────────────────────────
  if (OBLIGATION_STATUS_CODES.has(value)) {
    const key = `obligations:status_${value}`;
    const translated = i18n.t(key, { defaultValue: "" });
    return {
      display: translated || value,
      isEmpty: false,
      fullValue: value,
    };
  }

  // ── Known renewal cycle codes ───────────────────────────────────────────────
  if (CYCLE_CODES.has(value)) {
    const translated = i18n.t(`cycles:${value}`, { defaultValue: "" });
    return {
      display: translated || value,
      isEmpty: false,
      fullValue: value,
    };
  }

  // ── Long string truncation ──────────────────────────────────────────────────
  if (value.length > MAX_LENGTH) {
    return {
      display: value.slice(0, MAX_LENGTH) + "…",
      isEmpty: false,
      fullValue: value,
    };
  }

  // ── Money / percentage amounts: two decimals (exact value kept in fullValue) ─
  if (AMOUNT_FIELDS.has(_field) && PLAIN_NUMBER_RE.test(value)) {
    const formatted = formatAmount2(value);
    if (formatted !== null) {
      return { display: formatted, isEmpty: false, fullValue: value };
    }
  }

  // ── Verbatim (preserves legal phrasings per CLAUDE.md §6) ──────────────────
  return { display: value, isEmpty: false, fullValue: value };
}

// ── Day-grouping for the global History page ─────────────────────────────────

export interface DayGroup {
  /**
   * Human-readable day label resolved from i18n:
   * "Today" / "Yesterday" / localized date string.
   */
  label: string;
  /** ISO date YYYY-MM-DD — used as React key and <time dateTime> attribute. */
  dateKey: string;
  items: ChangeLog[];
}

/**
 * Groups a pre-sorted descending ChangeLog array into day buckets.
 * Used ONLY by History.tsx (global page). Not used in EntityDetail.
 *
 * @param items  Sorted descending by createdAt (as returned by /api/changelog).
 * @param lang   i18n.language string, e.g. "en" or "fr".
 */
export function groupByDay(items: ChangeLog[], lang: string): DayGroup[] {
  if (items.length === 0) return [];

  const todayKey = toLocalDateKey(new Date());
  const yesterdayDate = new Date();
  yesterdayDate.setDate(yesterdayDate.getDate() - 1);
  const yesterdayKey = toLocalDateKey(yesterdayDate);

  const buckets = new Map<string, ChangeLog[]>();
  const keyOrder: string[] = [];

  for (const item of items) {
    let dateKey: string;
    try {
      const d = new Date(item.createdAt);
      dateKey = isNaN(d.getTime()) ? "unknown" : toLocalDateKey(d);
    } catch {
      dateKey = "unknown";
    }
    if (!buckets.has(dateKey)) {
      buckets.set(dateKey, []);
      keyOrder.push(dateKey);
    }
    buckets.get(dateKey)!.push(item);
  }

  return keyOrder.map((dateKey) => {
    let label: string;
    if (dateKey === todayKey) {
      label = i18n.t("audit:day_today", { defaultValue: "Today" });
    } else if (dateKey === yesterdayKey) {
      label = i18n.t("audit:day_yesterday", { defaultValue: "Yesterday" });
    } else if (dateKey === "unknown") {
      label = "—";
    } else {
      try {
        label = new Date(dateKey).toLocaleDateString(lang, {
          day: "numeric",
          month: "short",
          year: "numeric",
        });
      } catch {
        label = dateKey;
      }
    }
    return { label, dateKey, items: buckets.get(dateKey)! };
  });
}

/** Returns a YYYY-MM-DD string in LOCAL time (not UTC). */
function toLocalDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// ─── Action classification ────────────────────────────────────────────────────

export type ActionType =
  | "created"
  | "updated"
  | "deleted"
  | "renewed"
  | "reassigned"
  | "exported"
  | "created-entity"
  | "system"
  | "confirmed";

export interface ActionMeta {
  type: ActionType;
  /** Lucide icon name (caller imports from lucide-react). */
  icon: string;
  /** Tailwind colour classes for the chip: "bg-... text-..." */
  chipClass: string;
  /** Tailwind class for the filled action circle background. */
  circleClass: string;
  /** Hex/var colour for the filled action circle (for inline style fallback). */
  circleBgVar: string;
}

/** Map each known field to its action type. Falls back to "updated". */
const FIELD_ACTION_MAP: Record<string, ActionType> = {
  // lifecycle
  entity_created: "created-entity",
  entity_deleted: "deleted",
  entity_restored: "created",
  entity_exported: "exported",
  bulk_exported: "exported",
  // directors
  director_created: "created",
  director_deleted: "deleted",
  director_backfill_split: "system",
  // obligations
  obligation_created: "created",
  obligation_deleted: "deleted",
  obligation_recomputed: "system",
  obligation_confirmed: "confirmed",
  mark_renewed: "renewed",
  // ownership
  ownership_stake_created: "created",
  ownership_stake_deleted: "deleted",
  // documents
  document_uploaded: "created",
  document_deleted: "deleted",
  // country/user/regulatory
  country_created: "created",
  user_role: "updated",
  regulatory_confirmed: "confirmed",
  regulatory_dismissed: "deleted",
  regulatory_applied: "confirmed",
  // reassignment
  region: "reassigned",
  countryId: "reassigned",
  "country.region": "reassigned",
};

const ACTION_META: Record<ActionType, ActionMeta> = {
  "created":        { type: "created",        icon: "Plus",           chipClass: "bg-green-100 text-green-700",   circleClass: "bg-green-600",  circleBgVar: "#059669" },
  "updated":        { type: "updated",         icon: "Pencil",         chipClass: "bg-blue-100 text-blue-700",    circleClass: "bg-blue-600",   circleBgVar: "#2563eb" },
  "deleted":        { type: "deleted",         icon: "Trash2",         chipClass: "bg-red-100 text-red-700",      circleClass: "bg-red-600",    circleBgVar: "#dc2626" },
  "renewed":        { type: "renewed",         icon: "RefreshCw",      chipClass: "bg-accent-soft text-accent",   circleClass: "bg-accent",     circleBgVar: "var(--accent)" },
  "reassigned":     { type: "reassigned",      icon: "ArrowRightLeft", chipClass: "bg-purple-100 text-purple-700",circleClass: "bg-purple-600", circleBgVar: "#7c3aed" },
  "exported":       { type: "exported",        icon: "Download",       chipClass: "bg-slate-100 text-slate-600",  circleClass: "bg-slate-600",  circleBgVar: "#475569" },
  "created-entity": { type: "created-entity",  icon: "Building2",      chipClass: "bg-green-100 text-green-700",  circleClass: "bg-green-600",  circleBgVar: "#059669" },
  "system":         { type: "system",          icon: "Cpu",            chipClass: "bg-surface-2 text-dim",        circleClass: "bg-[#a3a19c]",  circleBgVar: "#9b9a97" },
  "confirmed":      { type: "confirmed",       icon: "CheckCircle2",   chipClass: "bg-teal-100 text-teal-700",    circleClass: "bg-teal-600",   circleBgVar: "#14b8a6" },
};

export function classifyAction(field: string): ActionMeta {
  // Check the explicit map first
  if (field in FIELD_ACTION_MAP) {
    return ACTION_META[FIELD_ACTION_MAP[field]];
  }
  // Bracket-prefixed fields are system events
  if (field.startsWith("[")) return ACTION_META["system"];
  return ACTION_META["updated"];
}

/**
 * Returns a short verb phrase for the new History row layout.
 * e.g. "updated", "added", "removed", "renewed", "exported"
 */
export function getActionVerb(field: string): string {
  const action = classifyAction(field);
  const verbMap: Record<ActionType, string> = {
    "created":        "added",
    "updated":        "updated",
    "deleted":        "removed",
    "renewed":        "renewed",
    "reassigned":     "reassigned",
    "exported":       "exported",
    "created-entity": "created",
    "system":         "processed",
    "confirmed":      "confirmed",
  };
  return verbMap[action.type] ?? "updated";
}

/**
 * Build a plain-English one-line summary for a ChangeLog row.
 *
 * Examples:
 *   "Muhammed added director Roger OUDIZ to Strideforth LTD"
 *   "Marked Annual filing renewed — Hightekers GmbH"
 *   "Updated UBO on Hightekers Spain"
 *   "Created country Bahrain"
 *   "Promoted Roger OUDIZ to legal_advisor"
 */
export function buildSummary(item: ChangeLog): string {
  const actor = item.user?.name
    ?? item.user?.email
    ?? i18n.t("audit:attributed_to_legacy_operator", { defaultValue: "Operator" });
  const entityName = item.entity?.name ?? item.targetName ?? "";
  const fieldLabelStr = i18n.t(`fields:${item.field}`, { defaultValue: item.field });

  // Specific patterns
  switch (item.field) {
    case "entity_created":
      return i18n.t("audit:summary_entity_created", {
        defaultValue: `${actor} created entity ${entityName}`,
        actor, entity: entityName,
      });
    case "entity_deleted":
      return i18n.t("audit:summary_entity_deleted", {
        defaultValue: `${actor} deleted entity ${entityName}`,
        actor, entity: entityName,
      });
    case "entity_restored":
      return i18n.t("audit:summary_entity_restored", {
        defaultValue: `${actor} restored entity ${entityName}`,
        actor, entity: entityName,
      });
    case "director_created":
      return i18n.t("audit:summary_director_created", {
        defaultValue: `${actor} added director ${item.newValue ?? ""} to ${entityName}`,
        actor, director: item.newValue ?? "", entity: entityName,
      });
    case "director_deleted":
      return i18n.t("audit:summary_director_deleted", {
        defaultValue: `${actor} removed director ${item.oldValue ?? ""} from ${entityName}`,
        actor, director: item.oldValue ?? "", entity: entityName,
      });
    case "mark_renewed":
      return i18n.t("audit:summary_mark_renewed", {
        defaultValue: `Marked renewal — ${entityName}`,
        entity: entityName,
      });
    case "obligation_created":
      return i18n.t("audit:summary_obligation_created", {
        defaultValue: `${actor} created obligation on ${entityName}`,
        actor, entity: entityName,
      });
    case "obligation_deleted":
      return i18n.t("audit:summary_obligation_deleted", {
        defaultValue: `${actor} removed obligation from ${entityName}`,
        actor, entity: entityName,
      });
    case "country_created":
      return i18n.t("audit:summary_country_created", {
        defaultValue: `${actor} created country ${item.targetName ?? item.newValue ?? ""}`,
        actor, country: item.targetName ?? item.newValue ?? "",
      });
    case "user_role":
      return i18n.t("audit:summary_user_role", {
        defaultValue: `${actor} changed role of ${item.targetName ?? ""}: ${item.oldValue ?? "?"} → ${item.newValue ?? "?"}`,
        actor, user: item.targetName ?? "", oldRole: item.oldValue ?? "?", newRole: item.newValue ?? "?",
      });
    case "regulatory_confirmed":
      return i18n.t("audit:summary_regulatory_confirmed", {
        defaultValue: `${actor} confirmed regulatory candidate: ${item.targetName ?? ""}`,
        actor, candidate: item.targetName ?? "",
      });
    case "regulatory_dismissed":
      return i18n.t("audit:summary_regulatory_dismissed", {
        defaultValue: `${actor} dismissed regulatory candidate: ${item.targetName ?? ""}`,
        actor, candidate: item.targetName ?? "",
      });
    case "regulatory_applied":
      return i18n.t("audit:summary_regulatory_applied", {
        defaultValue: `${actor} applied regulatory change: ${item.targetName ?? ""}`,
        actor, candidate: item.targetName ?? "",
      });
    case "countryId":
      return i18n.t("audit:summary_country_reassigned", {
        defaultValue: `${actor} moved ${entityName} to ${item.newValue ?? "?"}`,
        actor, entity: entityName, country: item.newValue ?? "?",
      });
    case "ownership_stake_created":
      return i18n.t("audit:summary_ownership_created", {
        defaultValue: `${actor} added ownership stake to ${entityName}`,
        actor, entity: entityName,
      });
    case "ownership_stake_deleted":
      return i18n.t("audit:summary_ownership_deleted", {
        defaultValue: `${actor} removed ownership stake from ${entityName}`,
        actor, entity: entityName,
      });
    case "document_uploaded":
      return i18n.t("audit:summary_document_uploaded", {
        defaultValue: `${actor} uploaded document to ${entityName}`,
        actor, entity: entityName,
      });
    case "document_deleted":
      return i18n.t("audit:summary_document_deleted", {
        defaultValue: `${actor} deleted document from ${entityName}`,
        actor, entity: entityName,
      });
    case "entity_exported":
    case "bulk_exported":
      return i18n.t("audit:summary_exported", {
        defaultValue: `${actor} exported ${entityName}`,
        actor, entity: entityName,
      });
    case "obligation_confirmed":
      return i18n.t("audit:summary_obligation_confirmed", {
        defaultValue: `${actor} confirmed obligation on ${entityName}`,
        actor, entity: entityName,
      });
    case "obligation_recomputed":
      return i18n.t("audit:summary_obligation_recomputed", {
        defaultValue: `System recomputed obligations for ${entityName}`,
        entity: entityName,
      });
    case "director_backfill_split":
      return i18n.t("audit:summary_director_backfill_split", {
        defaultValue: `System migrated directors for ${entityName}`,
        entity: entityName,
      });
    default:
      if (entityName) {
        return i18n.t("audit:summary_updated_field", {
          defaultValue: `${actor} updated ${fieldLabelStr} on ${entityName}`,
          actor, field: fieldLabelStr, entity: entityName,
        });
      }
      return i18n.t("audit:summary_updated_field_no_entity", {
        defaultValue: `${actor} updated ${fieldLabelStr}`,
        actor, field: fieldLabelStr,
      });
  }
}

// ─── System-row detection ─────────────────────────────────────────────────────

// Fields written ONLY by background scripts / cron jobs / export trail — never
// by a human typing into a form. Anything NOT in this set is a human action
// (even when userId is null — covers the 48 legacy edits made before the
// attribution wiring shipped, where stale JWT cookies wrote userId=null).
const SYSTEM_FIELDS = new Set<string>([
  "obligation_recomputed",
  "reviewFlag",
  "entity_exported",
  "bulk_exported",
  "director_backfill_split",
]);

// Bracket-tag prefix used by system helpers in their comments (regwatch,
// ownership-sum-check). Only treated as a system signal when there is ALSO
// no user attribution — operator-triggered helpers (e.g. [mark-renewed]) keep
// userId populated and remain visible.
const SYSTEM_COMMENT_REGEX = /^\[[a-z][a-z0-9_-]*\]/;

/**
 * Target types that are NOT entity-data changes (governance + regulatory +
 * country administration). The History page surfaces only entity data changes,
 * so these are hidden. The backend is the primary filter (hideSystem); this
 * set is the frontend defence-in-depth. Entity-data rows have
 * targetType="entity" or null and are always kept.
 */
export const NON_DATA_TARGET_TYPES = new Set<string>([
  "user",
  "invitation",
  "regulatory",
  "country",
]);

/**
 * Returns true if this ChangeLog row is a "system" row that should be hidden
 * from the History / audit-timeline views.
 *
 * The decision is based on the FIELD name (and the helper-tag comment as a
 * tiebreaker), NOT on whether userId happens to be populated. Legacy operator
 * edits with userId=null (because the operator's JWT was minted before the
 * `id` claim was added) still pass through and show up — labelled "Operator"
 * via the buildSummary() fallback when no user is joined.
 */
export function isSystemRow(item: ChangeLog): boolean {
  if (SYSTEM_FIELDS.has(item.field)) return true;
  // Non-entity-data rows (governance / regulatory / country admin) are not
  // shown on the History page. Entity-data rows (targetType "entity" or null)
  // pass through.
  if (item.targetType != null && NON_DATA_TARGET_TYPES.has(item.targetType)) {
    return true;
  }
  if (
    item.userId == null
    && item.comment
    && SYSTEM_COMMENT_REGEX.test(item.comment.trim())
  ) {
    return true;
  }
  return false;
}

// ─── Collapse consecutive system runs ────────────────────────────────────────

export interface CollapsedSystemRun {
  type: "system-run";
  count: number;
  items: ChangeLog[];
}

export type TimelineItem = ChangeLog | CollapsedSystemRun;

export function isCollapsedSystemRun(item: TimelineItem): item is CollapsedSystemRun {
  return (item as CollapsedSystemRun).type === "system-run";
}

/**
 * Takes an array of ChangeLog items (already filtered for display — possibly
 * including system rows when showSystem=true) and collapses consecutive runs
 * of system rows into a single `CollapsedSystemRun` node.
 *
 * Non-system rows and isolated system rows are passed through unchanged.
 * A "run" requires >= 3 consecutive system rows to collapse.
 */
export function collapseSystemRuns(items: ChangeLog[]): TimelineItem[] {
  const result: TimelineItem[] = [];
  let run: ChangeLog[] = [];

  const flushRun = () => {
    if (run.length === 0) return;
    if (run.length >= 3) {
      result.push({ type: "system-run", count: run.length, items: [...run] });
    } else {
      result.push(...run);
    }
    run = [];
  };

  for (const item of items) {
    if (isSystemRow(item)) {
      run.push(item);
    } else {
      flushRun();
      result.push(item);
    }
  }
  flushRun();
  return result;
}
